import React, { useState, useEffect } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const CONFIRM_WORD = 'DELETE';

// Type-to-confirm dialog: the confirm button only works once the user types DELETE (exact caps).
export const ConfirmDeleteDialog = ({
  open, title = 'Delete?', itemName, description, confirmLabel = 'Delete', onClose, onConfirm, testId = 'confirm-delete',
}) => {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setTyped(''); setBusy(false); } }, [open]);

  const confirm = async () => {
    if (typed !== CONFIRM_WORD || busy) return;
    setBusy(true);
    try { await onConfirm(); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-w-md" data-testid={`${testId}-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" /> {title}
          </DialogTitle>
          <DialogDescription className="text-slate-600 pt-1">
            {itemName && (
              <span className="font-semibold text-slate-800" data-testid={`${testId}-dialog-name`}>"{itemName}"</span>
            )}{' '}
            {description}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); confirm(); }} className="space-y-3">
          <label className="block text-sm text-slate-700">
            Type <span className="font-mono font-bold text-red-600">{CONFIRM_WORD}</span> to confirm
          </label>
          <Input
            autoFocus
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={CONFIRM_WORD}
            autoComplete="off"
            autoCapitalize="characters"
            className="font-mono"
            data-testid={`${testId}-confirm-input`}
          />
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose} disabled={busy} data-testid={`${testId}-cancel-btn`}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={typed !== CONFIRM_WORD || busy}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid={`${testId}-confirm-btn`}
            >
              {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} {confirmLabel}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmDeleteDialog;
